import React from 'react';
import { ClipboardList, Truck } from 'lucide-react';
import { ItemOverviewOrderLineDto, ItemOverviewPurchaseLineDto } from '../../../../../types/itemOverview';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { StatusBadge } from '../../../../common/StatusBadge';
import { formatDate, formatQty } from '../format';
import { SectionCard } from './SectionCard';

// StatusType 1 — linjestatus (OrderLine/POrderLine)
const ST_LINE_STATUS = 1;

const th = 'px-3 py-2 text-xs font-medium text-gray-500 uppercase';

/** Lagersjef: åpne salgsordrelinjer med kunde (OrderLine.LineStatus < 40). */
export const ItemOrderLinesSection: React.FC<{ lines: ItemOverviewOrderLineDto[] }> = ({ lines }) => {
  const { t } = useTranslation();
  const k = (key: string, fallback: string) => t(`warehouse.modules.itemCard.orders.${key}`, {}, fallback);

  return (
    <SectionCard icon={<ClipboardList className="h-4 w-4" />} title={k('title', 'Åpne salgsordre')} count={lines.length}>
      {lines.length === 0 ? (
        <p className="text-sm text-gray-500 py-2">{k('empty', 'Ingen åpne salgsordre på varen')}</p>
      ) : (
        <div className="overflow-x-auto -mx-4 sm:-mx-5">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className={`${th} text-left`}>{k('order', 'Ordre')}</th>
                <th className={`${th} text-left`}>{k('customer', 'Kunde')}</th>
                <th className={`${th} text-left`}>{k('status', 'Status')}</th>
                <th className={`${th} text-left`}>{k('delivery', 'Levering')}</th>
                <th className={`${th} text-right`}>{k('ordered', 'Bestilt')}</th>
                <th className={`${th} text-right`}>{k('picked', 'Plukket')}</th>
                <th className={`${th} text-right`}>{k('remaining', 'Rest')}</th>
                <th className={`${th} text-left`}>{k('pickList', 'Plukkliste')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {lines.map(l => (
                <tr key={`${l.orderNo}-${l.lineNo}`} className="hover:bg-gray-50">
                  <td className="px-3 py-2 whitespace-nowrap font-medium">{l.orderNoERP || l.orderNo}<span className="text-gray-400"> /{l.lineNo}</span></td>
                  <td className="px-3 py-2 text-xs text-gray-700 max-w-[220px] truncate" title={l.customerName || ''}>
                    {l.customerName || l.customerNo || '-'}
                  </td>
                  <td className="px-3 py-2">{l.lineStatus != null ? <StatusBadge value={l.lineStatus} statusType={ST_LINE_STATUS} /> : '-'}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">{formatDate(l.deliveryDate)}</td>
                  <td className="px-3 py-2 text-right">{formatQty(l.quantity)}</td>
                  <td className="px-3 py-2 text-right text-blue-600">{formatQty(l.quantityPicked)}</td>
                  <td className="px-3 py-2 text-right font-semibold">{formatQty(l.quantityRemaining)}</td>
                  <td className="px-3 py-2 text-xs text-gray-600">{l.pListID ? `PL ${l.pListID}` : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
};

/** Lagersjef: åpne innkjøpslinjer med restantall (eksakt varenr). */
export const ItemPurchaseLinesSection: React.FC<{ lines: ItemOverviewPurchaseLineDto[] }> = ({ lines }) => {
  const { t } = useTranslation();
  const k = (key: string, fallback: string) => t(`warehouse.modules.itemCard.purchases.${key}`, {}, fallback);

  return (
    <SectionCard icon={<Truck className="h-4 w-4" />} title={k('title', 'Åpne innkjøp')} count={lines.length}>
      {lines.length === 0 ? (
        <p className="text-sm text-gray-500 py-2">{k('empty', 'Ingen åpne innkjøp på varen')}</p>
      ) : (
        <div className="overflow-x-auto -mx-4 sm:-mx-5">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className={`${th} text-left`}>{k('order', 'Bestilling')}</th>
                <th className={`${th} text-left`}>{k('supplier', 'Leverandør')}</th>
                <th className={`${th} text-left`}>{k('status', 'Status')}</th>
                <th className={`${th} text-left`}>{k('delivery', 'Levering')}</th>
                <th className={`${th} text-right`}>{k('ordered', 'Bestilt')}</th>
                <th className={`${th} text-right`}>{k('received', 'Mottatt')}</th>
                <th className={`${th} text-right`}>{k('remaining', 'Rest')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {lines.map(l => (
                <tr key={`${l.orderNo}-${l.lineNo}`} className="hover:bg-gray-50">
                  <td className="px-3 py-2 whitespace-nowrap font-medium">{l.orderNoERP || l.orderNo}<span className="text-gray-400"> /{l.lineNo}</span></td>
                  <td className="px-3 py-2 text-xs text-gray-700 max-w-[220px] truncate" title={l.supplierName || ''}>
                    {l.supplierName || l.supplierNo || '-'}
                  </td>
                  <td className="px-3 py-2">{l.lineStatus != null ? <StatusBadge value={l.lineStatus} statusType={ST_LINE_STATUS} /> : '-'}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">{formatDate(l.deliveryDate)}</td>
                  <td className="px-3 py-2 text-right">{formatQty(l.quantity)}</td>
                  <td className="px-3 py-2 text-right text-green-600">{formatQty(l.quantityDelivered)}</td>
                  <td className="px-3 py-2 text-right font-semibold">{formatQty(l.remainingQuantity)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
};
